import {useState, useRef, useCallback, useEffect} from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Stack, Typography, Button, IconButton, LinearProgress,
} from "@mui/material";
import {IconUpload, IconX, IconCheck, IconFile} from "@tabler/icons-react";
import {CircularProgress} from "@mui/material";
import {KnowledgeDatabaseRepository} from "../../../../repositories/KnowledgeDatabaseRepository";
import {useStompWebsocketRag} from "../../../../hooks/useStompWebsocketRag";

const ACCEPTED_EXTENSIONS = [".pdf", ".txt", ".md", ".csv", ".json", ".docx"];
const ACCEPTED_TYPES = [
    "application/pdf",
    "text/plain",
    "text/markdown",
    "text/csv",
    "application/json",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

export const DocumentUpload = ({topicId, open, onClose, onUploaded}) => {
    const [pendingFiles, setPendingFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const wsTopic = open && topicId ? `/topic/topics/${topicId}/documents` : null;
    const [updatedValue] = useStompWebsocketRag(wsTopic);

    useEffect(() => {
        if (!updatedValue) return;
        if (updatedValue.event !== "updated" || !updatedValue.document) return;

        setPendingFiles(prev =>
            prev.map(f =>
                f.uploadedDocId === updatedValue.document.id
                    ? {
                        ...f,
                        status: updatedValue.document.status === "indexed" ? "done"
                            : updatedValue.document.status === "failed" ? "error"
                                : "processing",
                    }
                    : f
            )
        );
    }, [updatedValue]);

    const addFiles = useCallback((files) => {
        const newFiles = Array.from(files)
            .filter(f => {
                const ext = "." + f.name.split(".").pop()?.toLowerCase();
                return ACCEPTED_EXTENSIONS.includes(ext) || ACCEPTED_TYPES.includes(f.type);
            })
            .map(f => ({
                file: f,
                id: `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                progress: 0,
                status: "pending",
            }));
        setPendingFiles(prev => [...prev, ...newFiles]);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
    }, [addFiles]);

    const removePending = (id) => setPendingFiles(prev => prev.filter(f => f.id !== id));

    const handleUpload = async () => {
        setIsUploading(true);
        const pending = pendingFiles.filter(f => f.status === "pending");

        for (const pf of pending) {
            try {
                const uploadedDoc = await KnowledgeDatabaseRepository.uploadDocument(topicId, pf.file, (pct) => {
                    setPendingFiles(prev =>
                        prev.map(f =>
                            f.id === pf.id
                                ? {...f, progress: pct, status: pct < 100 ? "uploading" : "processing"}
                                : f
                        )
                    );
                });

                setPendingFiles(prev =>
                    prev.map(f =>
                        f.id === pf.id
                            ? {...f, progress: 100, uploadedDocId: uploadedDoc.id, status: "processing"}
                            : f
                    )
                );
            } catch (err) {
                console.error("[DocumentUpload] upload failed:", err);
                setPendingFiles(prev =>
                    prev.map(f => (f.id === pf.id ? {...f, status: "error"} : f))
                );
            }
        }

        setIsUploading(false);
        onUploaded?.();
    };

    const handleClose = () => {
        if (!isUploading) {
            setPendingFiles([]);
            onClose();
        }
    };

    const pendingCount = pendingFiles.filter(f => f.status === "pending").length;
    const allDone = pendingFiles.length > 0 && pendingFiles.every(f => f.status === "done");

    return (
        <Dialog open={open} onClose={handleClose} maxWidth={"sm"} fullWidth>
            <DialogTitle sx={{fontWeight: 600, fontSize: 17}}>Upload Document</DialogTitle>
            <DialogContent>
                <Box
                    onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                        border: "2px dashed",
                        borderColor: isDragging ? "#000" : "divider",
                        bgcolor: isDragging ? "action.hover" : "transparent",
                        borderRadius: 2,
                        p: 4,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "border-color 0.15s, background-color 0.15s",
                    }}
                >
                    <IconUpload size={32} color={"#9ca3af"} style={{marginBottom: 8}}/>
                    <Typography fontSize={13}>
                        Drop files here or <Box component={"span"}
                                                sx={{textDecoration: "underline", fontWeight: 600}}>browse</Box>
                    </Typography>
                    <Typography fontSize={11} color={"text.primary"} sx={{mt: 0.5}}>
                        Supports PDF, TXT, MD, CSV, JSON, DOCX
                    </Typography>
                    <input
                        ref={fileInputRef}
                        type={"file"}
                        multiple
                        accept={ACCEPTED_EXTENSIONS.join(",")}
                        style={{display: "none"}}
                        onChange={(e) => {
                            if (e.target.files) addFiles(e.target.files);
                            e.target.value = "";
                        }}
                    />
                </Box>

                {pendingFiles.length > 0 && (
                    <Stack gap={1} sx={{mt: 2, maxHeight: 240, overflow: "auto"}}>
                        {pendingFiles.map(pf => (
                            <Stack
                                key={pf.id}
                                direction={"row"} alignItems={"center"} gap={1.5}
                                sx={{p: 1.25, borderRadius: 1.5, border: "1px solid", borderColor: "divider"}}
                            >
                                <IconFile size={16} color={"#9ca3af"}/>
                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <Typography fontSize={12} noWrap>{pf.file.name}</Typography>
                                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                                        <Typography fontSize={10} color={"text.secondary"}>
                                            {formatSize(pf.file.size)}
                                        </Typography>
                                        {pf.status === "uploading" && (
                                            <LinearProgress
                                                variant={"determinate"} value={pf.progress}
                                                sx={{
                                                    flex: 1,
                                                    height: 4,
                                                    borderRadius: 2,
                                                    "& .MuiLinearProgress-bar": {bgcolor: "#000"}
                                                }}
                                            />
                                        )}
                                        {pf.status === "processing" && (
                                            <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                <CircularProgress size={10}/>
                                                <Typography fontSize={10} color={"#a16207"}>Processing...</Typography>
                                            </Stack>
                                        )}
                                        {pf.status === "done" && (
                                            <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                <IconCheck size={12} color={"#15803d"}/>
                                                <Typography fontSize={10} color={"#15803d"}>Indexed</Typography>
                                            </Stack>
                                        )}
                                        {pf.status === "error" && (
                                            <Typography fontSize={10} color={"error.main"}>Failed</Typography>
                                        )}
                                    </Stack>
                                </Box>
                                {pf.status === "pending" && (
                                    <IconButton
                                        size={"small"}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removePending(pf.id);
                                        }}
                                    >
                                        <IconX size={14}/>
                                    </IconButton>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2.5}}>
                <Button
                    variant={"outlined"} onClick={handleClose} disabled={isUploading}
                    sx={{textTransform: "none",borderColor:"transparent", borderRadius: 2, color: "text.primary","&:hover": {
                            borderColor: "divider",
                        }}}
                >
                    {allDone ? "Close" : "Cancel"}
                </Button>
                {!allDone && (
                    <Button
                        variant={"contained"}
                        startIcon={isUploading ? <CircularProgress size={14} sx={{color: "#fff"}}/> :
                            <IconUpload size={16}/>}
                        onClick={handleUpload}
                        disabled={pendingCount === 0 || isUploading}
                        sx={{
                            bgcolor: "#000",
                            color: "#fff",
                            textTransform: "none",
                            borderRadius: 2,
                            boxShadow: "none",
                            "&:hover": {bgcolor: "#000"}
                        }}
                    >
                        {isUploading ? "Uploading..." : `Upload ${pendingCount} file(s)`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};